// The Model step's "Textbook model" source (Properties): a model of prayog_tb.textbook (GET /properties/textbook) with
// its parameters, built on the server by a properties.model job {"textbook": {name, params}}.
import { Button, Group, NumberInput, Select, SimpleGrid, Stack, Switch, Text } from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { get, post, type Job, type ProjectDetail } from "../../lib/api";
import type { NegfMemory } from "../../lib/negf";

export interface TextbookParam { label: string; default: number | boolean; unit: string; min: number | null; max: number | null; kind: "number" | "bool" }
export interface TextbookModel { title: string; about: string; params: Record<string, TextbookParam> }
export type TextbookCatalogue = Record<string, TextbookModel>;

export const textbookCatalogue = () => get<TextbookCatalogue>("/properties/textbook");

/** The parameters to send: those that differ from the model's defaults. */
export function changedParams(m: TextbookModel, values: Record<string, number | boolean>): Record<string, number | boolean> {
  return Object.fromEntries(Object.entries(values).filter(([k, v]) => m.params[k] && v !== m.params[k].default));
}

export function TextbookPanel({ project, mem, started }: {
  project: ProjectDetail; mem: NegfMemory; started: (j: Job, source: NegfMemory["source"]) => void;
}) {
  const cat = useQuery({ queryKey: ["properties-textbook"], queryFn: textbookCatalogue, staleTime: Infinity });
  const prev = mem.source?.kind === "textbook" ? mem.source : undefined;
  const [name, setName] = useState<string>(prev?.name ?? "haldane");
  const [values, setValues] = useState<Record<string, number | boolean>>(prev?.params ?? {});
  const model = cat.data?.[name];
  const value = (k: string) => (k in values ? values[k] : model!.params[k].default);
  const build = useMutation({
    mutationFn: () => {
      const params = changedParams(model!, Object.fromEntries(Object.keys(model!.params).map((k) => [k, value(k)])));
      return post<Job>("/jobs", { kind: "properties.model", project_id: project.id, params: { textbook: { name, params } } })
        .then((j) => ({ j, params }));
    },
    onSuccess: ({ j, params }) => started(j, { kind: "textbook", name, title: model!.title, params }),
  });
  if (cat.error) return <ErrorBox error={cat.error} />;
  if (!cat.data || !model) return null;
  return (
    <Stack gap="xs" data-testid="textbook-panel">
      <Select label="Model" value={name} allowDeselect={false} w={360}
        data={Object.entries(cat.data).map(([value, m]) => ({ value, label: m.title }))}
        onChange={(v) => { if (v) { setName(v); setValues({}); } }} data-testid="textbook-model" />
      <Text size="xs" c="dimmed">{model.about}</Text>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xs">
        {Object.entries(model.params).map(([k, p]) => p.kind === "bool" ? (
          <Switch key={k} mt="lg" label={p.label} checked={!!value(k)} onChange={(e) => setValues({ ...values, [k]: e.currentTarget.checked })} />
        ) : (
          <NumberInput key={k} size="xs" label={`${p.label}${p.unit ? ` (${p.unit})` : ""}`} value={value(k) as number}
            min={p.min ?? undefined} max={p.max ?? undefined} step={0.05} decimalScale={6}
            onChange={(v) => typeof v === "number" && setValues({ ...values, [k]: v })} />
        ))}
      </SimpleGrid>
      <Group><Button onClick={() => build.mutate()} loading={build.isPending} data-testid="textbook-build">Build the model</Button></Group>
      <ErrorBox error={build.error} />
    </Stack>
  );
}
